import { createSlice, type PayloadAction } from "@reduxjs/toolkit";
import { type IFile } from "src/interfaces";
import { getUserId } from "src/utils";
import { getPendingRequestAction } from "src/utils/getPendingRequestLabel";
import {
  getPendingRequestRoute,
  indexEntryIdTypes,
} from "src/utils/getPendingRequestRoute";

export interface IPendingRequest {
  req: string;
  name: string;
  data: string;
  status: string;
  created: number;
  uploaded?: number | null;
  error?: string | null;
  action: string;
  lbname?: string;
  route?: string | null;
  userid?: string | null;
  serverEntryId?: string | null;
  /** Queued by a different user, so this session must not replay it. */
  isOtherUser?: boolean;
  isAutosave?: boolean;
  /** SQLite row order, used to preserve actions queued in the same second. */
  queueOrder?: number;
  /** Requests in one consecutive run of the same entry action. */
  queuedCount?: number;
  queuedNoun?: string;
}

interface IPendingNewEntry {
  id: string;
  lbid: string;
  created: number;
}

interface IPendingFiles {
  entryId: string;
  files: IFile[];
}

interface PendingRequestsSlice {
  pendingRequests: IPendingRequest[];
  pendingWithdraws: string[];
  pendingNewEntries: IPendingNewEntry[];
  pendingFiles: IPendingFiles[];
  pendingVerify: Array<{
    lbeid: string | null;
    partid: string | null;
    meid: string | null;
  }>;
}

const initialState: PendingRequestsSlice = {
  pendingRequests: [],
  pendingWithdraws: [],
  pendingNewEntries: [],
  pendingFiles: [],
  pendingVerify: [],
};

const byCreatedDesc = <T extends { created: number }>(items: T[]) =>
  [...items].sort((a, b) => b.created - a.created);

const isActiveUploadStatus = (status: string) => status !== "done";

interface RawPendingUpload extends IPendingRequest {
  server_entry_id?: string | null;
  local_entry_id?: string | null;
}

const indexServerEntryIds = (rows: RawPendingUpload[]) => {
  const byLocalEntryId = new Map<string, string>();

  rows.forEach(row => {
    if (row.local_entry_id && row.server_entry_id) {
      byLocalEntryId.set(row.local_entry_id, row.server_entry_id);
    }
  });

  return byLocalEntryId;
};

const resolveServerEntryId = (
  pending: RawPendingUpload,
  byLocalEntryId: Map<string, string>,
) =>
  pending.server_entry_id ??
  (pending.local_entry_id
    ? byLocalEntryId.get(pending.local_entry_id)
    : undefined) ??
  null;

export const pendingRequestsSlice = createSlice({
  name: "verifySlice",
  initialState,
  reducers: {
    setPendingRequests: (state, action: PayloadAction<RawPendingUpload[]>) => {
      const pendingRequests: IPendingRequest[] = [];
      const pendingWithdraws: string[] = [];
      const pendingNewEntries: IPendingNewEntry[] = [];
      const newEntriesIds: string[] = [];
      const pendingFiles: IPendingFiles[] = [];
      let pendingVerify: Array<{
        lbeid: string | null;
        partid: string | null;
        meid: string | null;
      }> = [];

      const serverEntryIdByLocalEntryId = indexServerEntryIds(action.payload);
      const entryIdTypeByKey = indexEntryIdTypes(action.payload);

      action.payload.forEach(pending => {
        let body: Record<string, any>;
        try {
          body = JSON.parse(pending.data);
        } catch {
          return;
        }

        const splitted = pending.req.split("__");
        const request = splitted[0];
        const id = splitted[1];
        const isActive = isActiveUploadStatus(pending.status);
        const isOtherUser =
          Boolean(pending.userid) && pending.userid !== getUserId();
        const serverEntryId = resolveServerEntryId(
          pending,
          serverEntryIdByLocalEntryId,
        );

        pendingRequests.push({
          ...pending,
          serverEntryId,
          isAutosave:
            body.request === "auto-save-entry" ||
            body.request === "draft-save-entry",
          action: getPendingRequestAction(request, body),
          lbname: body._lbname,
          route: getPendingRequestRoute({
            req: pending.req,
            data: pending.data,
            server_entry_id: serverEntryId,
            local_entry_id: pending.local_entry_id,
            entryIdTypeByKey,
          }),
          isOtherUser,
        });

        if (!isActive) {
          return;
        }

        // The overlays below drive this user's UI - a withdrawn-looking entry,
        // a phantom entry in the picker - so another user's work is excluded.
        if (isOtherUser) {
          return;
        }

        if (!id) {
          return;
        }

        if (request === "withdraw-entry") {
          pendingWithdraws.push(id);
        } else if (request === "save-entry") {
          if (id.slice(0, 4) === "new=") {
            const lbid = id.split("&&")[1]?.slice(5);
            if (lbid && !newEntriesIds.includes(id)) {
              pendingNewEntries.push({
                id,
                lbid,
                created: pending.created,
              });
              newEntriesIds.push(id);
            }
          }
        } else if (request === "upload-file") {
          const entryId = body.meid ?? body.partid ?? body.lbeid;
          const files = (body.files ?? []).map(
            (f: { name: string; type: string }) => ({
              aid: Math.random(),
              date: new Date(pending.created),
              filename: f.name,
              type: f.type,
              deleted: 0,
              local: true,
            }),
          );
          pendingFiles.push({ entryId, files });
        } else if (request === "verify-entry") {
          if (body.params?.user === getUserId()) {
            pendingVerify = [...pendingVerify, ...(body.params.list ?? [])];
          }
        }
      });
      state.pendingRequests = byCreatedDesc(pendingRequests);
      state.pendingWithdraws = pendingWithdraws;
      state.pendingNewEntries = byCreatedDesc(pendingNewEntries);
      state.pendingFiles = pendingFiles;
      state.pendingVerify = pendingVerify;
    },
  },
});

export const { setPendingRequests } = pendingRequestsSlice.actions;

export default pendingRequestsSlice.reducer;
